import { useGetStatus } from "@elation/api-client/src/stitch-web";
import type { StatusSummary } from "@elation/api-client/src/stitch-web/models";

import { StatusPage } from "@src/deprecated/pages/Status";
import { PRODUCT_NAME } from "@src/shared/brand";

export function meta() {
  return [
    { title: "Status" },
    { name: "description", content: `${PRODUCT_NAME.singular} system status` },
  ];
}

export function clientLoader() {
  return {};
}

// Snapshot is refreshed server-side by a background job (~1m cadence), so a
// light client poll is enough to keep the page current without hammering the
// expensive checks — the read itself is a single cached lookup.
const POLL_INTERVAL_MS = 30_000;

// Shown until the first successful fetch resolves. The page short-circuits to
// its loading skeleton while `isLoading`, so these values are only a type-safe
// placeholder.
const UNKNOWN_STITCH_WEB: StatusSummary = {
  name: PRODUCT_NAME.singular,
  description: `${PRODUCT_NAME.singular} application availability and core dependencies.`,
  status: "unknown",
};

function errorMessage(err: unknown): string | null {
  const data = (err as { response?: { data?: unknown } } | null)?.response
    ?.data;
  if (data && typeof data === "object" && "message" in data) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  if (err instanceof Error && err.message.trim()) return err.message;
  return null;
}

export default function Status() {
  const { data, isLoading, isError, error } = useGetStatus({
    query: { refetchInterval: POLL_INTERVAL_MS },
  });

  const snapshot = data?.data;

  return (
    <StatusPage
      stitchWeb={snapshot?.stitchWeb ?? UNKNOWN_STITCH_WEB}
      connectors={snapshot?.connectors ?? []}
      checkedAt={snapshot?.checkedAt}
      accountProvisioning={snapshot?.accountProvisioning}
      isLoading={isLoading}
      error={
        isError
          ? (errorMessage(error) ?? "Unable to load system status.")
          : null
      }
    />
  );
}
